import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import { IconContainer, FocusWrapper } from '@g4/boneyard/lib/RadioGroup/Radio';

import { Block as LabelBlock } from '../Label';
import { RadioGroup as BoneyardRadioGroup } from '../Boneyard';
import getNamespacedFieldName from './getNamespacedFieldName';
import getFormPartAutomationHook from './getFormPartAutomationHook';
import { RadioIcons } from './Icons';
import styled, { css } from 'styled-components';
import getFormikFieldProps from './getFormikFieldProps';
import FieldError from './FieldError';
import useFastFieldShouldRenderFn from './useFastFieldShouldRenderFn';

const propTypes = {
  name: PropTypes.string.isRequired,
  namespace: PropTypes.string,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  automationHook: PropTypes.string,
  travelerDetails: PropTypes.string,
  layout: PropTypes.string,
  isNested: PropTypes.bool,
  autoSelectDefaultValue: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.any, value: PropTypes.string })
  ),
  a11yDescription: PropTypes.string,
  fieldLevelValidation: PropTypes.func,
  alignIconTop: PropTypes.bool,
  forceErrorStyles: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  resetOnUnmount: PropTypes.bool,
  showOwnErrorMessage: PropTypes.bool,
  // ... plus other Boneyard Radio Group fields
};

const RadioGroupField = ({
  name,
  label,
  options,
  namespace,
  isRequired,
  automationHook,
  a11yDescription,
  travelerDetails,
  forceErrorStyles,
  fieldLevelValidation,
  isNested = false,
  alignIconTop = false,
  resetOnUnmount = false,
  layout = 'horizontal',
  showOwnErrorMessage = true,
  onBlur = Function.prototype,
  onChange = Function.prototype,
  autoSelectDefaultValue = false,
  ...props
}) => {
  const fieldName = getNamespacedFieldName(name, namespace);
  const fieldAutomationHook = travelerDetails
    ? automationHook + '_' + travelerDetails
    : getFormPartAutomationHook(automationHook, name, namespace);

  const iconSize = isNested ? 16 : 24;
  // Need this in order to force a default selections when
  // there is no value set in Formik. This prop is used,
  // by FieldHandler to set that default value.
  const extraProps = autoSelectDefaultValue
    ? {
        autoSelectedDefaultValue: options[0].value,
      }
    : {};

  const shouldUpdate = useFastFieldShouldRenderFn(['validate']);
  return (
    <>
      {label && (
        <LabelBlock
          htmlFor={fieldName}
          isRequired={isRequired}
          automationHook={fieldAutomationHook}
        >
          {label}
        </LabelBlock>
      )}

      <Field
        name={fieldName}
        validate={fieldLevelValidation}
        shouldUpdate={shouldUpdate}
      >
        {({ field, form, meta }) => {
          const fieldWithCustomHandlers = getFormikFieldProps({
            fieldName,
            field,
            meta,
            forceErrorStyles,
            onChange: function customOnChange(value) {
              onChange(value);
              value && form.setFieldError(fieldName, undefined);
            },
            onBlur,
          });

          return (
            <>
              <RadioGroup
                aria-describedby={`${fieldName}_error`}
                isNested={isNested}
                alignIconTop={alignIconTop}
                layout={layout}
                options={options}
                a11yDescription={a11yDescription}
                {...props}
                iconChecked={<RadioIcons.Checked size={iconSize} />}
                iconUnchecked={<RadioIcons.Unchecked size={iconSize} />}
                {...extraProps}
                {...fieldWithCustomHandlers}
                automationHook={fieldAutomationHook}
              />
              {showOwnErrorMessage ? (
                <FieldError fieldName={fieldName} label={label} />
              ) : null}
            </>
          );
        }}
      </Field>
    </>
  );
};
RadioGroupField.propTypes = propTypes;
RadioGroupField.displayName = 'RadioGroupField';

const RadioGroup = styled(BoneyardRadioGroup)`
  ${IconContainer} {
    ${props =>
      (props.isNested || props.alignIconTop) &&
      css`
        align-self: flex-start;
      `}
  }
  ${FocusWrapper} {
    ${props =>
      (props.isNested || props.alignIconTop) &&
      css`
        align-self: flex-start;
      `}
  }
`;

export default React.memo(RadioGroupField);
